import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getAuthHeadersFromUserData } from '../../shared/utils/auth-headers.util';
import { LoggerService } from '../../services/logger.service';

export interface TemplateData {
  id: string;
  name: string;
  thumbnail_url: string;
  pages?: number; // Optional field for backward compatibility
}

export interface ApiResponse {
  status: boolean;
  message: string;
  data: TemplateData[];
}

export interface TemplateDetail {
  id: string;
  name: string;
  json_data: {
    page: {
      title: string;
      description?: string;
      layout?: {
        device?: string;
        grid_system?: string;
        theme?: {
          primary_color?: string;
          secondary_color?: string;
          background_color?: string;
          font_family?: string;
        };
      };
      sections?: any;
      [key: string]: any;
    };
    [key: string]: any;
  };
  html_data: string;
}

export interface TemplateDetailResponse {
  status: boolean;
  message: string;
  data: TemplateDetail[] | TemplateDetail; // Can be array or single object for backward compatibility
}

export interface WebsiteCreateRequest {
  name: string;
  template_id: string;
}

export interface WebsiteCreateResponse {
  status: boolean;
  message: string;
  data?: any;
}

/**
* Service for interacting with the Builder Templates and Website APIs: fetches templates, creates and updates websites and pages, and handles file uploads.
* @component
* @selector app-template-service
* @example
*   <app-template-service></app-template-service>
* @input none N/A - This service has no component inputs; it is intended to be injected via Angular's DI (providedIn: 'root').
* @output none N/A - This service does not emit Angular outputs; it returns RxJS Observables from its HTTP methods.
*
* Purpose:
*   Centralizes HTTP interactions with the builder backend (templates, website, website-pages, upload endpoints). Handles authentication headers, FormData creation for file and page payloads, and exposes typed Observable responses.
*
* Key properties:
*   - apiUrl (private): base URL for template-related endpoints.
*   - websiteApiUrl (private): base URL for website endpoints.
*   - websitePagesApiUrl (private): base URL for website page endpoints.
*
* Important helper:
*   - getAuthHeaders(includeJsonContentType: boolean = true) (private):
*       Returns HttpHeaders including authentication from user data. Caller controls whether to include JSON Content-Type (must be false for FormData requests).
*
* Key methods (returns Observables):
*   - getTemplates(): Observable<ApiResponse>
*       Fetches the list of available templates.
*   - getTemplateById(templateId: string): Observable<TemplateDetailResponse>
*       Fetches details for a single template by ID.
*   - createWebsite(request: WebsiteCreateRequest): Observable<WebsiteCreateResponse>
*       Creates a new website resource using JSON payload and authenticated headers.
*   - createWebsitePage(request: { template_page_id: string; json_data: any; website_id: string }): Observable<any>
*       Creates a website page. Converts json_data to string when needed and sends as FormData (do NOT set Content-Type manually).
*   - getWebsite(): Observable<any>
*       Checks if a website exists for the current user.
*   - getWebsiteById(websiteId: string): Observable<any>
*       Fetches a specific website by ID.
*   - updateWebsite(websiteId: string, formData: FormData): Observable<any>
*       Updates a website including file fields (e.g., brand logo). Uses FormData and logs request details for debugging.
*   - uploadWebsiteFile(file: File): Observable<any>
*       Uploads a file (image) for website assets; uses FormData and authenticated request.
*   - uploadPackageImage(packageId: string, file: File): Observable<any>
*       Uploads an image directly to a package endpoint for the given packageId.
*   - updateWebsitePage(websitePageId: string, jsonData: any, htmlData: string, websiteId: string): Observable<any>
*       Updates a website page's JSON and HTML; ensures jsonData is stringified and sends as FormData.
*   - updateWebsitePageStatus(websitePageId: string, status: boolean): Observable<any>
*       Toggles a page's active/inactive status using a JSON payload.
*   - resetWebsite(websiteId: string): Observable<any>
*       Resets a website to its last published version (DELETE).
*   - updateWebsiteDomain(payload: { domain_type: string; domain_name: string; website_id: string }): Observable<any>
*       Submits domain update requests for a website.
*
* Behavior notes and best practices:
*   - All network methods return Observables; callers should subscribe or use async pipes.
*   - For file/FormData uploads, do not manually set the Content-Type header; the browser will set multipart boundaries. Use getAuthHeaders(false) to avoid adding JSON content type.
*   - Methods include console.debug/log statements to aid troubleshooting (URLs, FormData entries, file sizes). Remove or guard logs in production if necessary.
*
* Lifecycle hooks:
*   - This is an Injectable service (providedIn: 'root') and does not implement Angular component lifecycle hooks.
*/
@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  private readonly logger = inject(LoggerService);
  private readonly apiRoot = `${environment.baseUrl}/${environment.apiVersion}`;
  private readonly apiUrl = `${environment.baseUrl}/${environment.apiVersion}/builder/templates`;
  private readonly websiteApiUrl = `${environment.baseUrl}/${environment.apiVersion}/builder/website`;
  private readonly websitePagesApiUrl = `${environment.baseUrl}/${environment.apiVersion}/builder/website-pages`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(includeJsonContentType = true): HttpHeaders {
    return getAuthHeadersFromUserData({ includeJsonContentType });
  }

  getTemplates(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(this.apiUrl, {
      headers: this.getAuthHeaders(false)
    });
  }

  getTemplateById(templateId: string): Observable<TemplateDetailResponse> {
    const url = `${this.apiUrl}/${templateId}`;
    return this.http.get<TemplateDetailResponse>(url, {
      headers: this.getAuthHeaders(false)
    });
  }

  createWebsite(request: WebsiteCreateRequest): Observable<WebsiteCreateResponse> {
    return this.http.post<WebsiteCreateResponse>(this.websiteApiUrl, request, {
      headers: this.getAuthHeaders(true)
    });
  }

  /**
  * Create or update a website page by sending multipart/form-data (template_page_id, json_data, website_id) to the website pages API.
  * @example
  * reateWebsitePage({ template_page_id: 'tpl123', json_data: { title: 'Home' }, website_id: 'site456' })
  * Observable<any>
  * @param {{ template_page_id: string; json_data: any; website_id: string }} {{request}} - Request payload containing template_page_id, json_data (object or string) and website_id.
  * @returns {{Observable<any>}} Observable that emits the HTTP response from the API.
  **/
  createWebsitePage(request: { template_page_id: string; json_data: any; website_id: string }): Observable<any> {
    const form = new FormData();
    form.append('template_page_id', request.template_page_id);
    // Ensure json_data is a string
    const jsonString = typeof request.json_data === 'string' ? request.json_data : JSON.stringify(request.json_data);
    form.append('json_data', jsonString);
    form.append('website_id', request.website_id);

    // Do NOT set Content-Type for FormData
    const headers = this.getAuthHeaders(false);
    return this.http.put<any>(this.websitePagesApiUrl, form, { headers });
  }

  // Check if a website already exists for the current user
  getWebsite(): Observable<any> {
    return this.http.get<any>(`${this.apiRoot}/builder/website`, {
      headers: this.getAuthHeaders(false)
    });
  }

  // Fetch a specific website by ID
  getWebsiteById(websiteId: string): Observable<any> {
    return this.http.get<any>(`${this.apiRoot}/builder/website/${websiteId}`, {
      headers: this.getAuthHeaders(false)
    });
  }

  // Update website (including brand_logo)
  /**
  * Update a website by sending FormData via HTTP PUT to the builder API.
  * @example
  * pdateWebsite('site123', formData)
  * Observable<any>
  * @param {string} websiteId - ID of the website to update.
  * @param {FormData} formData - FormData containing fields and files for the website update.
  * @returns {Observable<any>} Observable that emits the server response.
  **/
  updateWebsite(websiteId: string, formData: FormData): Observable<any> {
    // Don't set Content-Type for FormData - browser will set it with boundary
    const headers = this.getAuthHeaders(false);
    const url = `${this.apiRoot}/builder/website/${websiteId}`;
    
    this.logger.debug('TemplateService: updateWebsite called');
    this.logger.debug('URL:', url);
    this.logger.debug('Website ID:', websiteId);
    this.logger.debug('Headers:', headers.keys());
    this.logger.debug('FormData entries:');
    for (const pair of formData.entries()) {
      const value = pair[1] instanceof File 
        ? `File(name: ${pair[1].name}, size: ${pair[1].size} bytes, type: ${pair[1].type})` 
        : pair[1];
      this.logger.debug(`  ${pair[0]}:`, value);
    }
    
    return this.http.put<any>(url, formData, {
      headers
    });
  }

  // Upload logo/file for website
  /**
  * Upload a single website file (image) to the builder upload endpoint using FormData and authentication headers.
  * @example
  * ploadWebsiteFile(sampleFile)
  * Observable<any> // emits server response for the uploaded file
  * @param {{File}} {{file}} - The file object to upload (expects an image).
  * @returns {{Observable<any>}} Observable that emits the server response.
  **/
  uploadWebsiteFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('type', 'image');
    
    const headers = this.getAuthHeaders(false);
    const url = `${this.apiRoot}/builder/upload`;
    
    this.logger.debug('TemplateService: uploadWebsiteFile called');
    this.logger.debug('URL:', url);
    this.logger.debug(`File: ${file.name}`, { size: file.size });
    this.logger.debug('FormData entries:');
    for (const pair of formData.entries()) {
      this.logger.debug(`  ${pair[0]}:`, pair[1] instanceof File ? `File(${pair[1].name}, ${pair[1].size} bytes)` : pair[1]);
    }
    
    return this.http.post<any>(url, formData, {
      headers
    });
  }

  // Upload package image directly to package endpoint
  /**
  * Uploads an image file for a package by sending a multipart/form-data PUT request to the backend.
  * @example
  * ploadPackageImage('package-123', file)
  * Observable<any> // emits the HTTP response from the server
  * @param {{string}} {{packageId}} - The identifier of the package to associate the uploaded image with.
  * @param {{File}} {{file}} - The image file to upload.
  * @returns {{Observable<any>}} Observable that emits the server HTTP response for the upload request.
  **/
  uploadPackageImage(packageId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    
    const headers = this.getAuthHeaders(false);
    const url = `${this.apiRoot}/builder/package/${packageId}`;
    
    this.logger.debug('TemplateService: uploadPackageImage called');
    this.logger.debug('URL:', url);
    this.logger.debug('Package ID:', packageId);
    this.logger.debug(`File: ${file.name}`, { size: file.size });
    
    return this.http.put<any>(url, formData, {
      headers
    });
  }

  // Update website page (for updating json_data and html_data)
  /**
  * Uploads updated JSON and HTML for a website page using FormData and an authenticated PUT request.
  * @example
  * pdateWebsitePage('page123', { title: 'Home' }, '<div>Home</div>', 'website456')
  * Observable<any>
  * @param {{string}} {{websitePageId}} - The identifier of the website page to update.
  * @param {{any}} {{jsonData}} - The JSON data for the page (object or JSON string) that will be sent as json_data.
  * @param {{string}} {{htmlData}} - The HTML content for the page that will be sent as html_data.
  * @param {{string}} {{websiteId}} - The identifier of the website to which the page belongs.
  * @returns {{Observable<any>}} Observable emitting the HTTP response from the update request.
  **/
  updateWebsitePage(websitePageId: string, jsonData: any, htmlData: string, websiteId: string): Observable<any> {
    const formData = new FormData();
    formData.append('website_page_id', websitePageId);
    
    // Ensure json_data is a string
    const jsonString = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData);
    formData.append('json_data', jsonString);
    formData.append('html_data', htmlData);
    formData.append('website_id', websiteId);

    // Don't set Content-Type for FormData
    const headers = this.getAuthHeaders(false);
    
    this.logger.debug('TemplateService: updateWebsitePage called');
    this.logger.debug('URL:', this.websitePagesApiUrl);
    this.logger.debug('FormData entries:');
    for (const pair of formData.entries()) {
      const value = pair[1] instanceof File 
        ? `File(name: ${pair[1].name}, size: ${pair[1].size} bytes, type: ${pair[1].type})` 
        : (typeof pair[1] === 'string' && pair[1].length > 100 ? `${pair[1].substring(0, 100)}...` : pair[1]);
      this.logger.debug(`  ${pair[0]}:`, value);
    }
    
    return this.http.put<any>(this.websitePagesApiUrl, formData, {
      headers
    });
  }

  // Update website page status (active/inactive)
  /**
  * Update the status (enabled/disabled) of a website page by ID via API PUT request.
  * @example
  * pdateWebsitePageStatus('605c3f...', true)
  * Observable<any>
  * @param {{string}} {{websitePageId}} - ID of the website page to update.
  * @param {{boolean}} {{status}} - New status for the page (true = enabled, false = disabled).
  * @returns {{Observable<any>}} Observable wrapping the API response.
  **/
  updateWebsitePageStatus(websitePageId: string, status: boolean): Observable<any> {
    const url = `${this.apiRoot}/builder/website-pages/${websitePageId}/status`;
    const body = { status };
    const headers = this.getAuthHeaders(true);
    
    this.logger.debug('TemplateService: updateWebsitePageStatus called');
    this.logger.debug('URL:', url);
    this.logger.debug('Status:', status);
    
    return this.http.put<any>(url, body, {
      headers
    });
  }

  publishWebsite(websiteId: string): Observable<any> {
    const url = `${this.apiRoot}/builder/website/${websiteId}/publish`;
    const headers = this.getAuthHeaders(false);

    this.logger.debug('TemplateService: publishWebsite called');
    this.logger.debug('URL:', url);

    return this.http.put<any>(url, {}, { headers });
  }

  unpublishWebsite(websiteId: string): Observable<any> {
    const url = `${this.apiRoot}/builder/website/${websiteId}/unpublish`;
    const headers = this.getAuthHeaders(false);

    this.logger.debug('TemplateService: unpublishWebsite called');
    this.logger.debug('URL:', url);

    return this.http.put<any>(url, {}, { headers });
  }

  // Reset website to last published version
  /**
  * Resets a website by ID by calling the builder reset endpoint on the backend.
  * @example
  * esetWebsite('site123')
  * // Observable<any> that emits the server response for the reset operation
  * @param {{string}} {{websiteId}} - The ID of the website to reset.
  * @returns {{Observable<any>}} Observable<any> that emits the HTTP delete response.
  **/
  resetWebsite(websiteId: string): Observable<any> {
    const url = `${this.apiRoot}/builder/website/${websiteId}/reset`;
    const headers = this.getAuthHeaders(false);
    
    this.logger.debug('TemplateService: resetWebsite called');
    this.logger.debug('URL:', url);
    this.logger.debug('Website ID:', websiteId);
    
    return this.http.delete<any>(url, {
      headers
    });
  }

  updateWebsiteDomain(payload: { domain_type: string; domain_name: string; website_id: string }): Observable<any> {
    const url = `${this.apiRoot}/builder/website/domain`;
    const headers = this.getAuthHeaders(true);
    return this.http.post<any>(url, payload, { headers });
  }
}
